import type { ProcoreResponsibilityCandidate } from "../../../../shared/procoreDailyLog";

type DistributionInput = Pick<
  ProcoreResponsibilityCandidate,
  "score" | "confidencePercent"
>;

/**
 * Round float shares (that sum to ~100) to integers that sum to EXACTLY 100,
 * using the largest-remainder method so the rounding bias goes to the closest
 * candidates first.
 */
function roundToHundred(shares: number[]): number[] {
  const floors = shares.map((s) => Math.floor(s));
  const used = floors.reduce((sum, f) => sum + f, 0);
  let remaining = 100 - used;
  const byFraction = shares
    .map((s, i) => ({ i, frac: s - Math.floor(s) }))
    .sort((a, b) => b.frac - a.frac);
  const result = [...floors];
  for (let k = 0; remaining > 0 && byFraction.length > 0; k++, remaining--) {
    result[byFraction[k % byFraction.length].i] += 1;
  }
  return result;
}

/**
 * Bar percentages + lead flag for the "Assign to" distribution rows.
 *
 * The backend `confidencePercent` values are already calibrated (winner floored
 * at ≥92; ambiguous ties spread proportionally; weak partial-name matches
 * dampened) and normally sum to ~100. We display them AS-IS and only normalize
 * DOWN when they sum to more than 100 (legacy raw-score resolutions stored
 * before calibration). We never inflate a sub-100 total back up — dampened weak
 * matches must be allowed to read as weak (e.g. two "John" cards at 13%/12%),
 * not re-normalized into a confident 50/50. Falls back to `score`, then an even
 * split, when confidence is absent.
 */
export function assigneeDistributionPercentages(
  candidates: DistributionInput[],
): { pct: number; lead: boolean }[] {
  if (candidates.length === 0) {
    return [];
  }

  const values = candidates.map((c) => {
    if (typeof c.confidencePercent === "number" && Number.isFinite(c.confidencePercent) && c.confidencePercent >= 0) {
      return c.confidencePercent;
    }
    if (typeof c.score === "number" && Number.isFinite(c.score) && c.score > 0) {
      return c.score;
    }
    return 0;
  });

  const total = values.reduce((sum, v) => sum + v, 0);
  const pcts =
    total > 100
      ? // Legacy raw scores that sum well over 100 — normalize to shares.
        roundToHundred(values.map((v) => (v / total) * 100))
      : total > 0
        ? // Calibrated values (incl. dampened sub-100 totals) — display as-is.
          values.map((v) => Math.round(v))
        : candidates.some(c => typeof c.confidencePercent === "number" && Number.isFinite(c.confidencePercent) && c.confidencePercent >= 0)
          ? values.map(() => 0)
          : roundToHundred(candidates.map(() => 100 / candidates.length));

  const top = Math.max(...pcts);
  const second = [...pcts].sort((a, b) => b - a)[1] ?? 0;
  // A clear leader only when it dominates — otherwise the distribution is flat.
  const hasLead = top >= 40 && top - second >= 10;

  return pcts.map((pct) => ({ pct, lead: hasLead && pct === top }));
}
